import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FormField,
  TextInput,
  FormGroupCard,
  SelectField,
  CategoryBrowser,
} from "src/shared/forms/components";
import type { CategoryItem } from "src/shared/forms/components/CategoryBrowser";
import type { WizardStepComponentProps } from "src/shared/forms/types";
import type { ProductWizardData } from "../../../hooks/useProductWizard";
import {
  useAccountCategories,
  useAccountCategoryAttributes,
} from "../../../../category/hooks/useAccountCategories";
import type {
  MarketplaceType,
  AccountCategoryAttribute,
  AccountCategory,
} from "../../../../category/types";
import type { RootState } from "../../../../../../setup/redux/store";
import AccountCategoryService from "../../../../category/services/accountCategoryService";
import { getCategoryIdFromPath } from "../../../../category/utils/categoryTreeBuilder";

interface CategoryStepProps extends WizardStepComponentProps {
  data: ProductWizardData["category"];
}

// Convert AccountCategory to CategoryItem for CategoryBrowser
const toCategoryItem = (cat: AccountCategory): CategoryItem => ({
  id: cat.id,
  name: cat.name,
  hasChildren: cat.hasChildren,
  isActive: cat.isActive,
  parentCategoryId: cat.parentCategoryId,
});

// Convert CategoryItem back to AccountCategory
const toAccountCategory = (item: CategoryItem): AccountCategory => ({
  id: item.id,
  name: item.name,
  hasChildren: item.hasChildren ?? false, // Default to false if not provided
  isActive: item.isActive ?? true, // Default to true if not provided
  parentCategoryId: item.parentCategoryId,
});

const CategoryStep: React.FC<CategoryStepProps> = ({
  data,
  onDataChange,
  allData,
  errors = [],
}) => {
  const attributes = data.attributes || {};

  const [isCategoryBrowserOpen, setIsCategoryBrowserOpen] = useState(false);
  const [rootCategories, setRootCategories] = useState<CategoryItem[]>([]);
  const [rootLoading, setRootLoading] = useState(false);
  const [rootError, setRootError] = useState<string | null>(null);
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Initialize selectedPath directly from initialCategoryPath if available
  const [selectedPath, setSelectedPath] = useState<CategoryItem[]>(data.initialCategoryPath?.map(toCategoryItem) || []);

  const accounts = useSelector((state: RootState) => state.Account.accounts);
  const selectedAccount = accounts.find(
    (acc) => acc.id === allData?.basicInfo?.selectedStore
  );
  const marketplace = selectedAccount?.marketplace as MarketplaceType;

  // Use account category attributes hook
  const {
    attributes: categoryAttributes,
    loading: attributesLoading,
    error: attributesError,
    fetchAttributes,
  } = useAccountCategoryAttributes(getCategoryIdFromPath(data.initialCategoryPath || []));

  // Effect to handle initial category path fetching if only leaf is provided
  useEffect(() => {
    let isMounted = true;

    if (data.initialCategoryPath && data.initialCategoryPath.length > 0 && selectedPath.length === 0 && marketplace) {
      console.log('[CategoryStep] Auto-fetch useEffect triggered - selectedPath is empty but initialCategoryPath exists');
      const leafCategory = data.initialCategoryPath[data.initialCategoryPath.length - 1];
      // If it's a leaf-only path, or we need to confirm the full path, fetch it
      if (!leafCategory.hasChildren || data.initialCategoryPath.length === 1) {
        console.log('[CategoryStep] Fetching full category path for leaf:', leafCategory.id);
        const fetchFullCategoryPath = async () => {
          try {
            const service = AccountCategoryService.getInstance();
            const response = await service.getCategoryPath(marketplace, leafCategory.id);
            if (isMounted && response?.data?.path) {
              const pathItems = response.data.path.map(toCategoryItem);
              console.log('[CategoryStep] Fetched full path, updating selectedPath:', pathItems.map(c => c.id));
              setSelectedPath(pathItems);
              // Update the form data with the full path (not just leaf ID)
              updateCategoryData({ initialCategoryPath: response.data.path });
            }
          } catch (err) {
            if (isMounted) {
              console.error("Failed to fetch category path:", err);
            }
          }
        };
        fetchFullCategoryPath();
      } else {
        // Assume initialCategoryPath is already the full path if provided and not just a leaf
        console.log('[CategoryStep] Using initialCategoryPath as-is (not fetching)');
        if (isMounted) {
          setSelectedPath(data.initialCategoryPath.map(toCategoryItem));
        }
        // No need to update categoryId - initialCategoryPath is already set
      }
    }

    return () => {
      isMounted = false;
    };
  }, [data.initialCategoryPath, marketplace, selectedPath.length]);

  // Load root categories when marketplace changes
  useEffect(() => {
    let isMounted = true;

    const loadRootCategories = async () => {
      if (
        !marketplace ||
        !["shopee", "lazada", "tiktok"].includes(marketplace)
      ) {
        if (isMounted) {
          setRootCategories([]);
        }
        return;
      }

      if (isMounted) {
        setRootLoading(true);
        setRootError(null);
      }

      try {
        const service = AccountCategoryService.getInstance();
        const response = await service.getRootCategories(marketplace);
        if (isMounted) {
          setRootCategories(response.data.categories.map(toCategoryItem));
        }
      } catch (err) {
        if (isMounted) {
          setRootError("Failed to load categories");
          setRootCategories([]);
        }
      } finally {
        if (isMounted) {
          setRootLoading(false);
        }
      }
    };

    loadRootCategories();

    return () => {
      isMounted = false;
    };
  }, [marketplace]);

  // Mark initial mount as complete after first render
  useEffect(() => {
    setIsInitialMount(false);
  }, []);

  // Fetch attributes when category changes
  useEffect(() => {
    const categoryId = getCategoryIdFromPath(data.initialCategoryPath || []);
    if (categoryId && marketplace) {
      fetchAttributes(marketplace, categoryId);
    }
  }, [data.initialCategoryPath, marketplace, fetchAttributes]);

  // Clear category when marketplace changes (but not on initial mount in edit mode)
  useEffect(() => {
    const categoryId = getCategoryIdFromPath(data.initialCategoryPath || []);
    if (!isInitialMount && marketplace && categoryId) {
      // Clear selection when marketplace changes (user action, not initial load)
      updateCategoryData({ initialCategoryPath: [] });
      setSelectedPath([]);
    }
  }, [marketplace]);

  const updateCategoryData = (
    updates: Partial<ProductWizardData["category"]>
  ) => {
    onDataChange({
      ...data,
      ...updates,
    });
  };

  const updateAttributes = (
    attributeUpdates: Partial<ProductWizardData["category"]["attributes"]>
  ) => {
    onDataChange({
      ...data,
      attributes: {
        ...(data.attributes || {}),
        ...attributeUpdates,
      },
    });
  };

  const basicInfo = allData?.basicInfo || {};

  // Load children for a category (callback for CategoryBrowser)
  const loadChildren = useCallback(
    async (parentId: string): Promise<CategoryItem[]> => {
      if (!marketplace) return [];

      try {
        const service = AccountCategoryService.getInstance();
        const response = await service.getChildCategories(
          marketplace,
          parentId
        );
        console.log('[CategoryStep] loadChildren API response for parentId:', parentId, {
          count: response.data.categories.length,
          firstCategory: response.data.categories[0],
          hasChildrenFields: response.data.categories.map(c => ({ id: c.id, hasChildren: c.hasChildren }))
        });
        const mapped = response.data.categories.map(toCategoryItem);
        console.log('[CategoryStep] loadChildren mapped result:', {
          count: mapped.length,
          firstMapped: mapped[0]
        });
        return mapped;
      } catch (err) {
        console.error("Failed to load child categories:", err);
        return [];
      }
    },
    [marketplace]
  );

  // Handle category selection from CategoryBrowser
  const handleCategoryChange = (categoryPath: CategoryItem[]) => {
    console.log('[CategoryStep] handleCategoryChange called with path:', categoryPath.map(c => c.id));
    if (categoryPath.length === 0) {
      updateCategoryData({ initialCategoryPath: [] });
      setSelectedPath([]);
      return;
    }

    // Convert CategoryItem[] to AccountCategory[] before storing
    const accountCategoryPath = categoryPath.map(toAccountCategory);
    updateCategoryData({ initialCategoryPath: accountCategoryPath });
    setSelectedPath(categoryPath);
  };

  // Get display name for the selected category
  const getSelectedCategoryDisplayName = () => {
    if (selectedPath.length > 0) {
      return selectedPath.map((cat) => cat.name).join(" > ");
    }
    return "";
  };

  // Render dynamic attribute field based on attribute type
  const renderAttributeField = (attr: AccountCategoryAttribute) => {
    const value = attributes[attr.originalAttributeName] || "";

    switch (attr.inputType) {
      case "DROP_DOWN":
      case "COMBO_BOX":
        return (
          <SelectField
            value={value as string}
            onChange={(v) =>
              updateAttributes({ [attr.originalAttributeName]: v })
            }
            placeholder={`Select ${attr.displayAttributeName}...`}
            searchable={true}
            options={[
              {
                value: "",
                label: `Select ${attr.displayAttributeName}...`,
                disabled: true,
              },
              ...(attr.attributeValues || []).map((av) => ({
                value: av.originalValueName,
                label: av.displayValueName,
              })),
            ]}
            className="tw-w-full"
          />
        );
      case "NUMBER_FIELD":
        return (
          <TextInput
            value={value as string}
            onChange={(v) =>
              updateAttributes({ [attr.originalAttributeName]: v })
            }
            placeholder={`Enter ${attr.displayAttributeName}...`}
            className="tw-w-full"
          />
        );
      case "TEXT_FIELD":
      default:
        return (
          <TextInput
            value={value as string}
            onChange={(v) =>
              updateAttributes({ [attr.originalAttributeName]: v })
            }
            placeholder={`Enter ${attr.displayAttributeName}...`}
            className="tw-w-full"
          />
        );
    }
  };

  // Get placeholder text for category button
  const getCategoryPlaceholder = () => {
    if (!basicInfo.selectedStore) {
      return "Select a store first";
    }
    if (rootError) {
      return "Error loading categories";
    }
    return "Browse categories...";
  };

  const selectedCategoryDisplayName = getSelectedCategoryDisplayName();

  return (
    <div className="tw-space-y-6" data-testid="wizard-step-category">
      {/* Categorization & Classification Group */}
      <FormGroupCard
        title="Categorization & Classification"
        subtitle="Select category and brand to help customers find your product."
        data-testid="wizard-category-categorization-group"
      >
        <div className="tw-grid tw-gap-6">
          {/* Category Selection */}
          <FormField
            name="category"
            label="Category"
            required
            error={errors.find((e) => e.includes("category"))}
          >
            {/* Category selector button */}
            <button
              type="button"
              onClick={() => setIsCategoryBrowserOpen(true)}
              disabled={!basicInfo.selectedStore || rootLoading || !!rootError}
              className={`
                tw-w-full tw-bg-white dark:tw-bg-transparent
                tw-border tw-border-gray-300 dark:tw-border-[#364152]
                tw-rounded-lg tw-px-4 tw-py-2.5
                tw-text-left tw-flex tw-justify-between tw-items-center
                tw-transition-colors
                hover:tw-border-primary dark:hover:tw-border-primary
                focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent
                disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-border-gray-300
              `}
              data-testid="wizard-category-selector-button"
            >
              <span
                className={
                  selectedCategoryDisplayName
                    ? "tw-text-gray-900 dark:tw-text-white"
                    : "tw-text-gray-500 dark:tw-text-gray-400"
                }
              >
                {rootLoading ? (
                  <span className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-animate-spin tw-h-4 tw-w-4 tw-border-2 tw-border-primary tw-border-t-transparent tw-rounded-full"></div>
                    Loading categories...
                  </span>
                ) : (
                  selectedCategoryDisplayName || getCategoryPlaceholder()
                )}
              </span>
              <span className="material-symbols-outlined tw-text-gray-400">
                {rootLoading ? "" : "view_column"}
              </span>
            </button>

            {/* CategoryBrowser Modal */}
            {isCategoryBrowserOpen && (
              <>
                {console.log('[CategoryStep] Opening CategoryBrowser with:', {
                  selectedPathLength: selectedPath.length,
                  selectedPath: selectedPath.map(c => ({ id: c.id, name: c.name })),
                  rootCategoriesCount: rootCategories.length,
                  isCategoryBrowserOpen
                })}
              </>
            )}
            <CategoryBrowser
              rootCategories={rootCategories}
              rootLoading={rootLoading}
              loadChildren={loadChildren}
              selectedCategory={selectedPath}
              onChange={handleCategoryChange}
              isOpen={isCategoryBrowserOpen}
              onClose={() => {
                console.log('[CategoryStep] CategoryBrowser onClose called');
                setIsCategoryBrowserOpen(false);
              }}
            />

            {rootError && (
              <div className="tw-mt-2 tw-text-sm tw-text-red-600">
                Error loading categories: {rootError}
              </div>
            )}
            {!basicInfo.selectedStore && (
              <div className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1">
                Please select a store in the previous step to load categories.
              </div>
            )}
            {marketplace &&
              rootCategories.length === 0 &&
              !rootLoading &&
              !rootError && (
                <div className="tw-text-xs tw-text-orange-600 tw-mt-1">
                  No categories found for {marketplace}. Please contact support.
                </div>
              )}
          </FormField>

          {/* Brand Selection */}
          <FormField
            name="brand"
            label="Brand"
            required
            error={errors.find((e) => e.includes("brand"))}
          >
            <SelectField
              value={data.brandId || ""}
              onChange={(value) =>
                updateCategoryData({ brandId: value as string })
              }
              placeholder="Search for a brand..."
              searchable={true}
              options={[
                { value: "", label: "Search for a brand...", disabled: true },
                { value: "apple", label: "Apple" },
                { value: "samsung", label: "Samsung" },
                { value: "xiaomi", label: "Xiaomi" },
                { value: "oppo", label: "OPPO" },
                { value: "vivo", label: "Vivo" },
              ]}
              className="tw-w-full"
            />
          </FormField>
        </div>
      </FormGroupCard>

      {/* Product Attributes - Only show when category is selected */}
      {getCategoryIdFromPath(data.initialCategoryPath || []) && (
        <FormGroupCard
          title="Product Attributes"
          subtitle="Define basic product attributes and specifications."
          data-testid="wizard-category-attributes-group"
        >
          {/* Loading state */}
          {attributesLoading && (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
              <div className="tw-animate-spin tw-h-6 tw-w-6 tw-border-2 tw-border-primary tw-border-t-transparent tw-rounded-full"></div>
              <span className="tw-ml-3 tw-text-gray-500">
                Loading category attributes...
              </span>
            </div>
          )}

          {/* Error state */}
          {attributesError && !attributesLoading && (
            <div className="tw-text-sm tw-text-red-600 tw-py-4">
              Error loading category attributes: {attributesError}
            </div>
          )}

          {/* All Attributes (required + optional) */}
          {!attributesLoading &&
            !attributesError &&
            categoryAttributes.length > 0 && (
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                {categoryAttributes.map((attr) => (
                  <FormField
                    key={attr.attributeId}
                    name={attr.originalAttributeName}
                    label={attr.displayAttributeName}
                    required={attr.isMandatory}
                  >
                    {renderAttributeField(attr)}
                  </FormField>
                ))}
              </div>
            )}

          {/* No attributes found */}
          {!attributesLoading &&
            !attributesError &&
            categoryAttributes.length === 0 && (
              <div className="tw-text-sm tw-text-gray-500 tw-py-4">
                This category does not require additional attributes.
              </div>
            )}
        </FormGroupCard>
      )}
    </div>
  );
};

export default CategoryStep;
